import { getTranslation } from 'common/react/utils/translations';

const FIRST_PLACE = 0;
const SECOND_PLACE = 1;
const THIRD_PLACE = 2;
const FOURTH_PLACE = 3;
const FIFTH_PLACE = 4;
const SIXTH_PLACE = 5;
const SEVENTH_PLACE = 6;
const EIGTH_PLACE = 7;

// Fictional round numbers. Needed for finals predictions sorting.
export const WILDCARD_ROUND = 1000;
export const FINALS_WEEK_ONE_ROUND = 1001;
export const SEMI_FINALS_ROUND = 1002;
export const PRELIMINARY_FINALS_ROUND = 1003;
export const GRAND_FINAL_ROUND = 1004;

// Fictional match ids.
const WILDCARD_INITIAL_ID = 20000;
const FIRST_WEEK_INITIAL_ID = 10000;
const SEMI_FINALS_INITIAL_ID = 10004;
const PREL_FINALS_INITIAL_ID = 10006;
const GRAND_FINAL_INITIAL_ID = 10008;

/**
 * Creates the wildcard round matches based on the ladder teams in wildcard positions.
 *
 * @param {Array} bracket
 * @param {Object} cutOffPositions - The object containing the cutoff positions.
 *
 * @return {Array} - The wildcard round matches.
 */
export function createWildcardRound(bracket, cutOffPositions) {
    let roundId = WILDCARD_INITIAL_ID;
    const rounds = [];

    // Get positions of teams in wildcard round based on cutoffs.
    const wildcardTeams = [];

    for (let index = 0; index <= cutOffPositions.secondCutOff - 1; index++) {
        if (
            index > cutOffPositions.firstCutOff - 1 &&
            index < cutOffPositions.secondCutOff
        ) {
            wildcardTeams.push(bracket[index]);
        }
    }

    // For wildcard teams, 1st with 4th and 2nd with 3rd.
    const customPairs = [
        [0, 3, getTranslation('label.ladderPredictor.finalsGame.Wildcard1')], // 1st with 4th
        [1, 2, getTranslation('label.ladderPredictor.finalsGame.Wildcard2')] // 2nd with 3rd
    ];

    for (const [homeIndex, awayIndex, roundName] of customPairs) {
        const homeTeam = wildcardTeams[homeIndex];
        const awayTeam = wildcardTeams[awayIndex];
        const round = _createFinalsRound(
            homeTeam,
            awayTeam,
            WILDCARD_ROUND,
            roundName
        );
        round.id = roundId++;
        rounds.push(round);
    }

    return rounds;
}

/**
 * Creates the finals bracket from the first eight teams of the ladder.
 *
 * @param {Array} bracket - The array of the first eight teams.
 *
 * @returns {Array} - The array of finals rounds.
 */
export function createBracket(bracket, cutOffPositions, predictions) {
    let roundId = FIRST_WEEK_INITIAL_ID;
    const rounds = [];
    let customPairs = [];

    // If the wildcard round exists, this bracket also contains winners from the wildcard round.
    // If not, this bracket only contains the top x teams from the ladder based on the cutoff.

    if (cutOffPositions?.secondCutOff) {
        customPairs = [
            [
                FIRST_PLACE,
                FOURTH_PLACE,
                getTranslation(
                    'label.ladderPredictor.finalsGame.1stQualifyingFinal'
                )
            ], // 1st with 4th
            [
                FIFTH_PLACE,
                'wildcardLow',
                getTranslation(
                    'label.ladderPredictor.finalsGame.1stEliminationFinal'
                )
            ], // 5th with lowest ranked wildcard winner
            [
                SIXTH_PLACE,
                'wildcardHigh',
                getTranslation(
                    'label.ladderPredictor.finalsGame.2ndEliminationFinal'
                )
            ], // 6th with highest ranked wildcard winner
            [
                SECOND_PLACE,
                THIRD_PLACE,
                getTranslation(
                    'label.ladderPredictor.finalsGame.2ndQualifyingFinal'
                )
            ] // 2nd with 3rd
        ];

        // Get wildcard winners and sort them by their ladder position
        const wildcardWinners = predictions
            ?.filter(
                (prediction) =>
                    prediction.n ===
                        getTranslation(
                            'label.ladderPredictor.finalsGame.Wildcard1'
                        ) ||
                    prediction.n ===
                        getTranslation(
                            'label.ladderPredictor.finalsGame.Wildcard2'
                        )
            )
            .sort((a, b) => b.p - a.p);

        for (const [homeIndex, awayIndex, roundName] of customPairs) {
            const homeTeam = bracket[homeIndex];
            let awayTeamId = null;

            if (awayIndex === 'wildcardLow') {
                awayTeamId = wildcardWinners?.[0]?.w;
            }

            if (awayIndex === 'wildcardHigh') {
                awayTeamId = wildcardWinners?.[1]?.w;
            }

            const awayTeam = awayTeamId
                ? bracket.find((item) => item.team.id === awayTeamId)
                : bracket[awayIndex];

            const round = _createFinalsRound(
                homeTeam,
                awayTeam,
                SEMI_FINALS_ROUND,
                roundName
            );
            round.id = roundId++;
            rounds.push(round);
        }
    } else {
        customPairs = [
            [
                FIRST_PLACE,
                FOURTH_PLACE,
                getTranslation(
                    'label.ladderPredictor.finalsGame.1stQualifyingFinal'
                )
            ], // 1st with 4th
            [
                FIFTH_PLACE,
                EIGTH_PLACE,
                getTranslation(
                    'label.ladderPredictor.finalsGame.1stEliminationFinal'
                )
            ], // 5th with 8th
            [
                SIXTH_PLACE,
                SEVENTH_PLACE,
                getTranslation(
                    'label.ladderPredictor.finalsGame.2ndEliminationFinal'
                )
            ], // 6th with 7th
            [
                SECOND_PLACE,
                THIRD_PLACE,
                getTranslation(
                    'label.ladderPredictor.finalsGame.2ndQualifyingFinal'
                )
            ] // 2nd with 3rd
        ];

        for (const [homeIndex, awayIndex, roundName] of customPairs) {
            const homeTeam = bracket[homeIndex];
            const awayTeam = bracket[awayIndex];
            const round = _createFinalsRound(
                homeTeam,
                awayTeam,
                FINALS_WEEK_ONE_ROUND,
                roundName
            );
            round.id = roundId++;
            rounds.push(round);
        }
    }

    return rounds;
}

/**
 *  Generates the Semi-Finals matches based on Finals week one predictions.
 *
 * @param {Array} predictions - The Finals week one predictions.
 * @param {Array} bracket - The bracket. Used to get the team information.
 *
 * @returns {Array} - The Semi-Finals rounds.
 */
export function createSemiFinals(predictions, bracket) {
    let roundId = SEMI_FINALS_INITIAL_ID;
    const rounds = [];
    const customPairs = [
        [
            getTranslation(
                'label.ladderPredictor.finalsGame.1stQualifyingFinal'
            ),
            getTranslation(
                'label.ladderPredictor.finalsGame.1stEliminationFinal'
            ),
            getTranslation('label.ladderPredictor.finalsGame.1stSemiFinal')
        ],
        [
            getTranslation(
                'label.ladderPredictor.finalsGame.2ndQualifyingFinal'
            ),
            getTranslation(
                'label.ladderPredictor.finalsGame.2ndEliminationFinal'
            ),
            getTranslation('label.ladderPredictor.finalsGame.2ndSemiFinal')
        ]
    ];

    for (const [homeType, awayType, roundName] of customPairs) {
        const homeTeamId = predictions?.find(
            (prediction) => prediction.n === homeType
        )?.l;
        const awayTeamId = predictions?.find(
            (prediction) => prediction.n === awayType
        )?.w;
        const homeTeam = bracket?.find((item) => item.team.id === homeTeamId);
        const awayTeam = bracket?.find((item) => item.team.id === awayTeamId);

        const round = _createFinalsRound(
            homeTeam,
            awayTeam,
            SEMI_FINALS_ROUND,
            roundName
        );
        round.id = roundId++;
        rounds.push(round);
    }

    return rounds;
}

/**
 *  Generates the Preliminary Finals matches based on Semi-Finals predictions.
 *
 * @param {Array} predictions - The predictions.
 * @param {Array} bracket - The bracket. Used to get the team information.
 *
 * @returns {Array} - The Preliminary Finals rounds.
 */
export function createPreliminaryFinals(predictions, bracket) {
    let roundId = PREL_FINALS_INITIAL_ID;
    const rounds = [];
    const customPairs = [
        [
            getTranslation(
                'label.ladderPredictor.finalsGame.1stQualifyingFinal'
            ),
            getTranslation('label.ladderPredictor.finalsGame.2ndSemiFinal'),
            getTranslation(
                'label.ladderPredictor.finalsGame.1stPreliminaryFinal'
            )
        ],
        [
            getTranslation(
                'label.ladderPredictor.finalsGame.2ndQualifyingFinal'
            ),
            getTranslation('label.ladderPredictor.finalsGame.1stSemiFinal'),

            getTranslation(
                'label.ladderPredictor.finalsGame.2ndPreliminaryFinal'
            )
        ]
    ];

    for (const [homeType, awayType, roundName] of customPairs) {
        const homeTeamId = predictions?.find(
            (prediction) => prediction.n === homeType
        )?.w;
        const awayTeamId = predictions?.find(
            (prediction) => prediction.n === awayType
        )?.w;
        const homeTeam = bracket?.find((item) => item.team.id === homeTeamId);
        const awayTeam = bracket?.find((item) => item.team.id === awayTeamId);
        const round = _createFinalsRound(
            homeTeam,
            awayTeam,
            PRELIMINARY_FINALS_ROUND,
            roundName
        );
        round.id = roundId++;
        rounds.push(round);
    }

    return rounds;
}

/**
 *  Generates the Grand Final match based on the Preliminary Finals predictions.
 *
 * @param {Array} predictions - The predictions.
 * @param {Array} bracket - The bracket. Used to get the team information.
 *
 * @returns {object} - The Grand Final match.
 */
export function createGrandFinal(predictions, bracket) {
    let roundId = GRAND_FINAL_INITIAL_ID;
    const matchDetails = {
        homeType: getTranslation(
            'label.ladderPredictor.finalsGame.1stPreliminaryFinal'
        ),
        awayType: getTranslation(
            'label.ladderPredictor.finalsGame.2ndPreliminaryFinal'
        ),
        roundName: getTranslation('label.ladderPredictor.columns.grandFinal')
    };

    const homeTeamId = predictions?.find(
        (prediction) => prediction.n === matchDetails.homeType
    )?.w;
    const awayTeamId = predictions?.find(
        (prediction) => prediction.n === matchDetails.awayType
    )?.w;

    const homeTeam = bracket?.find((item) => item.team.id === homeTeamId);
    const awayTeam = bracket?.find((item) => item.team.id === awayTeamId);

    const grandFinal = _createFinalsRound(
        homeTeam,
        awayTeam,
        GRAND_FINAL_ROUND,
        matchDetails.roundName
    );
    grandFinal.id = roundId;
    return grandFinal;
}

/**
 *  Creates the winner based on the Preliminary Finals predictions.
 *
 * @param {Array} predictions - The predictions.
 * @param {Array} bracket - The bracket. Used to get the team information.
 *
 * @returns {object} - Returns the winner object.
 */
export function createWinner(predictions, bracket) {
    const winnerId = predictions?.find(
        (prediction) =>
            prediction.n ===
            getTranslation('label.ladderPredictor.columns.grandFinal')
    )?.w;

    const winner = bracket?.find((item) => item.team.id === winnerId);

    return winner;
}

/**
 * Gets the first teams of the ladder.
 *
 * @param {Array} ladder - The ladder rankings we will use to get the first teams.
 * @param {number} cutOff - The cutoff position to determine how many teams to return.
 * @returns {Array} - The teams that the bracket consists of.
 */
export function getTeams(ladder, cutOff) {
    return ladder.slice(0, cutOff);
}

/**
 * Creates the finals rounds.
 *
 * @param {object} homeTeam - The home team.
 * @param {object} awayTeam - The away team.
 * @param {number} roundNumber - The round number.
 * @param {string} roundName - The round name.
 * @returns {object} - The round object.
 */
function _createFinalsRound(homeTeam, awayTeam, roundNumber, roundName) {
    return {
        away: {
            ...awayTeam
        },
        home: {
            ...homeTeam
        },
        id: 0,
        round: {
            roundNumber: roundNumber,
            name: roundName
        }
    };
}

/**
 *
 * @param {Array} matchesData - An array of matches returned from an API call.
 * @param {object} predictedMatch - The object of the created match.
 * @param {Function} setterFunction - The callback function that dispatches
 * the setPredictionFinals reducer.
 */
export function handleConcludedMatch(
    matchesData,
    predictedMatch,
    setterFunction
) {
    matchesData?.forEach((matchData) => {
        if (
            matchData?.status ===
            PULSE.app.common.CONSTANTS.MATCH_STATUS.CONCLUDED
        ) {
            if (matchData?.home?.team?.id === predictedMatch?.home?.team?.id) {
                if (
                    matchData?.home?.score?.totalScore >
                        matchData?.away?.score?.totalScore &&
                    matchData?.home?.team
                ) {
                    setterFunction(
                        matchData?.home?.team?.id,
                        matchData?.away?.team?.id,
                        matchData
                    );
                } else if (
                    matchData?.away?.score?.totalScore >
                        matchData?.home?.score?.totalScore &&
                    matchData?.away?.team
                ) {
                    setterFunction(
                        matchData?.away?.team?.id,
                        matchData?.home?.team?.id,
                        matchData
                    );
                }
            }
        }
    });
}
